import React from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { addToast, removeToast } from '@mobi/component-library/Feedback/Toast'
import type { ToastItem } from '@mobi/component-library/Feedback/Toast/types'
import { Button } from '@mobi/component-library/Common/V2/Buttons'
import { Tray } from '@mobi/component-library/Overlays/Tray'
import type { Ticket } from '@core/Areas/AccountActivity/types'
import { AppRoutes } from '@core/AppRoutes'
import { queryClient } from '@core/Data/ReactQuery'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { appHistory } from '@core/Utils/routing/history'
import { postMultipleSharesToFollowMe } from '@core/Areas/FollowMe/helpers/api'
import { ShareBetSelection } from '@core/Areas/FollowMe/Components/QuickShare/Components/ShareBetSelection'
import { followMePrincipleProfileQuery } from '@core/Areas/FollowMe/helpers/queries'
import { FirstPostConfirmation } from '../Common/FirstPostConfirmation'

const enum LocalConstants {
  QuickShareToastId = 'bet-loop-quick-share',
}

export const QuickShare: React.FC<{ isOpen: boolean; onDismiss(): void }> = ({
  isOpen,
  onDismiss,
}) => {
  const [isFirstShareModalOpen, setIsFirstShareModalOpen] = React.useState(false)
  const [shouldHidePostButton, setShouldHidePostButton] = React.useState(false)
  const [ticketsToShare, setTicketsToShare] = React.useState<TicketsToShare>(() => new Set())

  React.useEffect(() => {
    if (isOpen) return

    return () => {
      setIsFirstShareModalOpen(false)
      setTicketsToShare(new Set())
      setShouldHidePostButton(false)
    }
  }, [isOpen])

  const { data: profileData, isLoading: isLoadingProfile } = useQuery(followMePrincipleProfileQuery)

  const { mutate: onPostToBetLoopMutate, isPending } = useMutation({
    mutationFn: () => postMultipleSharesToFollowMe({ ticketSequences: Array.from(ticketsToShare) }),
    onSuccess: response => {
      handleShareToBetLoopMutateResponse(response)
      queryClient.invalidateQueries({ queryKey: queryKeys.followMeSharePendingTickets })
    },
    onMutate() {
      setIsFirstShareModalOpen(false)
      onDismiss()
    },
    onError: handleShareToBetLoopMutateError,
  })

  const handleTicketClick = (ticketSequence: Ticket['ticketSequence']) => {
    if (isPending) return
    setTicketsToShare(prevTickets => {
      const newTickets = new Set(prevTickets)
      newTickets.has(ticketSequence)
        ? newTickets.delete(ticketSequence)
        : newTickets.add(ticketSequence)
      return newTickets
    })
  }

  const handleEmptyState = () => setShouldHidePostButton(true)

  const handleShareToBetLoopButtonClick = () => {
    profileData?.shareCount === 0 ? setIsFirstShareModalOpen(true) : onPostToBetLoopMutate()
  }

  return createPortal(
    <Tray
      isOpen={isOpen}
      headerTitle='Pending'
      onDismiss={onDismiss}
      footerComponent={
        shouldHidePostButton ? null : (
          <Button
            size='md'
            isFullWidth
            color='primary'
            isLoading={isPending}
            onClick={handleShareToBetLoopButtonClick}
            disabled={ticketsToShare.size === 0 || isLoadingProfile}
          >
            Share {ticketsToShare.size > 1 ? `${ticketsToShare.size} Bets` : 'Bet'} to Bet Loop
          </Button>
        )
      }
    >
      <>
        <ShareBetSelection
          ticketsToShare={ticketsToShare}
          onEmptyState={handleEmptyState}
          onTicketClick={handleTicketClick}
        />

        <FirstPostConfirmation
          isOpen={isFirstShareModalOpen}
          onConfirm={() => onPostToBetLoopMutate()}
          onCancel={() => setIsFirstShareModalOpen(false)}
        />
      </>
    </Tray>,
    document.body
  )
}

// =============
// Local Helpers
// =============

function handleShareToBetLoopMutateResponse({
  successfulShares,
  failedShares,
}: Awaited<ReturnType<typeof postMultipleSharesToFollowMe>>) {
  const hasSuccessfulPosts = successfulShares > 0
  const hasFailedPosts = failedShares > 0
  const wereAllPostsSuccessful = hasSuccessfulPosts && !hasFailedPosts

  let button: ToastItem['button']
  const icon = wereAllPostsSuccessful ? 'success' : hasSuccessfulPosts ? 'warning' : 'error'
  const title = hasSuccessfulPosts
    ? `${successfulShares} Bet${successfulShares > 1 ? 's' : ''} shared to Bet Loop`
    : 'Oops, unable to share to Bet Loop'

  if (hasSuccessfulPosts) {
    queryClient.invalidateQueries({
      queryKey: queryKeys.followMeSharedBetsFeedBase(),
      refetchActive: true,
    })
    button = {
      text: 'View',
      onClick() {
        appHistory.push(AppRoutes.BetLoopMyProfile)
        removeToast({ id: LocalConstants.QuickShareToastId })
      },
    }
  }

  addToast({
    id: LocalConstants.QuickShareToastId,
    timeoutMs: 8_000,
    title,
    icon,
    button,
    isDismissable: !button,
    theme: 'dark',
  })
}

function handleShareToBetLoopMutateError() {
  addToast({
    id: 'bet-loop-quick-share--error',
    timeoutMs: 6_000,
    title: 'Oops, unable to share to Bet Loop',
    icon: 'error',
    theme: 'dark',
  })
}

// =====
// Types
// =====

type TicketsToShare = Set<Ticket['ticketSequence']>
